import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { tablesDB, DB_ID, PRODUCTS_TABLE } from "../../appwrite";
import { Query, Permission, Role } from "appwrite";
import {
  Pencil,
  Trash2,
  Search,
  PackagePlus,
  AlertTriangle,
} from "lucide-react";
import { toast } from "react-toastify";
import "./AllProduct.css";
import { useTitle } from "../../hooks/useTitle";

const PER_PAGE = 10;

const fmtCategory = (slug) =>
  (slug || "")
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const fmtPrice = (price) =>
  "₦" +
  parseFloat(price || 0).toLocaleString("en-NG", { minimumFractionDigits: 0 });

export default function AllProduct() {
  useTitle("All Products - TrendBay Admin");
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await tablesDB.listRows({
          databaseId: DB_ID,
          tableId: PRODUCTS_TABLE,
          queries: [Query.orderDesc("$createdAt"), Query.limit(1000)],
        });
        const rows = (res.rows || res.documents || []).map((r) => ({
          id: r.$id,
          ...r,
        }));
        setProducts(rows);
        setFiltered(rows);
      } catch {
        toast.error("Failed to load products");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const q = query.toLowerCase();
    const result = products.filter(
      (p) =>
        (p.productName || "").toLowerCase().includes(q) ||
        (p.category || "").toLowerCase().includes(q) ||
        (p.sku || "").toLowerCase().includes(q),
    );
    setFiltered(result);
    setPage(1);
  }, [query, products]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const slice = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const toggleField = async (id, field, value) => {
    try {
      await tablesDB.updateRow({
        databaseId: DB_ID,
        tableId: PRODUCTS_TABLE,
        rowId: id,
        data: { [field]: value },
      });
      const updater = (arr) =>
        arr.map((p) => (p.id === id ? { ...p, [field]: value } : p));
      setProducts(updater);
      setFiltered(updater);
    } catch {
      toast.error("Update failed");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await tablesDB.deleteRow({
        databaseId: DB_ID,
        tableId: PRODUCTS_TABLE,
        rowId: deleteTarget.id,
      });
      const remover = (arr) => arr.filter((p) => p.id !== deleteTarget.id);
      setProducts(remover);
      setFiltered(remover);
      toast.success("Product deleted");
    } catch {
      toast.error("Failed to delete product");
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="alp-page">
      {/* Header */}
      <div className="alp-header">
        <div>
          <h2 className="alp-title">All Products</h2>
          <p className="alp-subtitle">
            {filtered.length} product{filtered.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button
          className="alp-add-btn"
          onClick={() => navigate("/d/add-product")}
        >
          <PackagePlus size={16} /> Add Product
        </button>
      </div>

      {/* Search */}
      <div className="alp-search-wrap">
        <Search size={16} className="alp-search-icon" />
        <input
          className="alp-search"
          type="text"
          placeholder="Search by name, category or SKU…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="alp-table-wrap">
        {loading ? (
          <div className="alp-loading">Loading products…</div>
        ) : filtered.length === 0 ? (
          <div className="alp-empty">No products found</div>
        ) : (
          <table className="alp-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Product</th>
                <th>Category</th>
                <th>SKU</th>
                <th>Price</th>
                <th>Available</th>
                <th>Featured</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {slice.map((product, idx) => (
                <tr key={product.id}>
                  <td className="alp-td-num">
                    {(page - 1) * PER_PAGE + idx + 1}
                  </td>
                  <td>
                    <div className="alp-product-cell">
                      <img
                        className="alp-thumb"
                        src={product.productImage}
                        alt={product.productName}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                      <span className="alp-product-name">
                        {product.productName || product.productName || "-"}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className="alp-cat-badge">
                      {fmtCategory(product.category || product.category)}
                    </span>
                  </td>
                  <td className="alp-sku">
                    {product.sku || product.sku || "-"}
                  </td>
                  <td className="alp-price">
                    {fmtPrice(product.price || product.price)}
                  </td>
                  <td>
                    <label className="alp-switch">
                      <input
                        type="checkbox"
                        checked={!!product.available}
                        onChange={() =>
                          toggleField(
                            product.id,
                            "available",
                            !product.available,
                          )
                        }
                      />
                      <span className="alp-switch-track" />
                    </label>
                  </td>
                  <td>
                    <label className="alp-switch">
                      <input
                        type="checkbox"
                        checked={!!product.featured}
                        onChange={() =>
                          toggleField(product.id, "featured", !product.featured)
                        }
                      />
                      <span className="alp-switch-track" />
                    </label>
                  </td>
                  <td>
                    <div className="alp-actions">
                      <button
                        className="alp-btn-edit"
                        onClick={() =>
                          navigate(`/d/edit-product/${product.id}`)
                        }
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        className="alp-btn-del"
                        onClick={() => setDeleteTarget(product)}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Mobile cards (hidden on desktop via CSS) */}
      <div className="alp-mobile-cards">
        {!loading &&
          slice.map((product) => (
            <div className="alp-mobile-card" key={product.id}>
              <div className="alp-mobile-card__main">
                <img
                  className="alp-thumb"
                  src={product.productImage}
                  alt={product.productName}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <div className="alp-mobile-card__info">
                  <p className="alp-mobile-card__name">
                    {product.productName || "-"}
                  </p>
                  <span className="alp-cat-badge">
                    {fmtCategory(product.category)}
                  </span>
                </div>
              </div>
              <div className="alp-mobile-card__footer">
                <span className="alp-price">{fmtPrice(product.price)}</span>
                <div className="alp-actions">
                  <button
                    className="alp-btn-edit"
                    onClick={() => navigate(`/d/edit-product/${product.id}`)}
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    className="alp-btn-del"
                    onClick={() => setDeleteTarget(product)}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        {!loading && filtered.length === 0 && (
          <div className="alp-empty">No products found</div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="alp-pagination">
          <button
            className="alp-page-btn"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            ← Prev
          </button>
          <span className="alp-page-info">
            Page {page} of {totalPages}
          </span>
          <button
            className="alp-page-btn"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next →
          </button>
        </div>
      )}

      {/* Delete modal */}
      {deleteTarget && (
        <div
          className="alp-modal-overlay"
          onClick={() => setDeleteTarget(null)}
        >
          <div className="alp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="alp-modal-icon">
              <AlertTriangle size={28} color="#e74c3c" />
            </div>
            <h3 className="alp-modal-title">Delete product?</h3>
            <p className="alp-modal-body">
              <strong>
                {deleteTarget.productName || deleteTarget.productName}
              </strong>{" "}
              will be permanently removed.
            </p>
            <div className="alp-modal-actions">
              <button
                className="alp-modal-cancel"
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </button>
              <button className="alp-modal-confirm" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
